import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Leaf, ArrowRight, KeyRound } from "lucide-react";
import CountdownClock from "@/components/CountdownClock";
import FeaturedDocument from "@/components/FeaturedDocument";
import PublicPaidTally from "@/components/PublicPaidTally";
import WallOfFamilies from "@/components/WallOfFamilies";

export default function Landing() {
  const [token, setToken] = useState("");
  const navigate = useNavigate();

  const openPortal = (e) => {
    e.preventDefault();
    const t = token.trim();
    if (!t) {
      toast.error("Paste the private link or code your organizer sent you.");
      return;
    }
    // If a full URL is pasted, extract the last path segment.
    const clean = t.includes("/family/") ? t.split("/family/").pop() : t;
    navigate(`/family/${clean}`);
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Top bar */}
      <header className="max-w-6xl mx-auto px-6 md:px-10 pt-8 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
          <span className="font-serif text-lg sm:text-2xl text-stone-900 truncate">Bailey Family Reunion</span>
        </div>
        <Link
          to="/admin/login"
          data-testid="header-admin-login-link"
          className="shrink-0 label-eyebrow text-stone-500 hover:text-[#1a2947] transition-colors text-right"
        >
          <span className="hidden sm:inline">Organizer </span>Sign In
        </Link>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 grid md:grid-cols-2 gap-14 items-center">
        <div>
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className="label-eyebrow">A gathering, remembered.</span>
            <span className="text-[10px] tracking-[0.16em] uppercase font-semibold text-white bg-[#1a2947] px-3 py-1 rounded-full">
              Denver · July 21-23, 2028
            </span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-stone-900 tracking-tight">
            The Bailey Family,{" "}
            <span className="italic text-[#1a2947]">bringing family together.</span>
          </h1>

          <CountdownClock />

          <div>
            <FeaturedDocument />
          </div>

          <PublicPaidTally />

          <WallOfFamilies />

          <p className="mt-6 text-lg text-stone-600 max-w-lg">
            This is the family hub for the <span className="font-medium text-stone-800">Bailey Family Reunion</span> — 
            addresses, kids, birthdays and reunion payments, kept fresh 
            by everyone who shows up.
          </p>

          <form
            onSubmit={openPortal}
            className="mt-10 bg-white border border-[#e6e2d6] rounded-xl p-2 flex items-center gap-2 shadow-[0_4px_20px_-4px_rgba(44,42,38,0.08)]"
            data-testid="magic-link-form"
          >
            <KeyRound className="h-5 w-5 text-stone-400 ml-2" />
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your private family link or code"
              className="border-0 focus-visible:ring-0 bg-transparent text-base"
              data-testid="magic-link-input"
            />
            <Button
              type="submit"
              className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill px-5"
              data-testid="magic-link-submit"
            >
              Open <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </form>
          <p className="mt-3 text-xs text-stone-500">
            Don't have a link? Email{" "}
            <a
              href="mailto:daniel2d@hotmail.com"
              className="text-[#1a2947] font-medium hover:underline"
              data-testid="hero-request-link-email"
            >
              daniel2d@hotmail.com
            </a>{" "}
            with your first and last name. A link will be sent to your email
            address so you can fill out your information.
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Organizing this year?{" "}
            <Link
              to="/admin/login"
              className="text-[#1a2947] font-medium hover:underline"
              data-testid="hero-organizer-link"
            >
              Head to Organizer Sign In →
            </Link>
          </p>

          <div className="mt-8 pt-6 border-t border-[#e6e2d6]">
            <div className="text-xs text-stone-500 mb-2">Explore</div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/statistics"
                data-testid="hero-statistics-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                See reunion stats →
              </Link>
              <Link
                to="/memoriam"
                data-testid="hero-memoriam-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                In Memoriam →
              </Link>
              <Link
                to="/payments"
                data-testid="hero-payments-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                Send payment →
              </Link>
              <Link
                to="/dues"
                data-testid="hero-dues-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                Reunion dues →
              </Link>
              <Link
                to="/documents"
                data-testid="hero-documents-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                Documents →
              </Link>
              <Link
                to="/reunion-photos"
                data-testid="hero-reunion-photos-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                Reunion album →
              </Link>
              <Link
                to="/family/login"
                data-testid="hero-family-signin-link"
                className="inline-flex items-center gap-2 rounded-full text-[#1a2947] font-medium px-4 py-2.5 hover:underline"
              >
                Family sign-in →
              </Link>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-[#eae5d9] rounded-[28px] rotate-[-1.5deg]" />
          <div className="relative rounded-[24px] w-full h-[460px] bg-gradient-to-br from-[#f5efe0] via-[#faf7ef] to-[#eae5d9] flex items-center justify-center shadow-[0_20px_60px_-20px_rgba(26,41,71,0.35)] overflow-hidden">
            {/* Denver mountain silhouette watermark */}
            <svg
              className="absolute bottom-0 left-0 w-full h-[58%] text-[#1a2947]/[0.13]"
              viewBox="0 0 400 200"
              fill="currentColor"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <polygon points="0,200 60,120 100,155 150,80 200,140 240,95 290,150 335,100 400,160 400,200" />
              <polygon points="0,200 40,150 90,175 140,130 180,165 220,115 270,170 320,140 360,175 400,155 400,200" opacity="0.55" />
            </svg>
            <img
              src="/bailey-logo.png"
              alt="Bailey Family Reunion · Denver 2028 badge"
              className="relative max-h-[92%] max-w-[92%] object-contain drop-shadow-[0_8px_20px_rgba(26,41,71,0.18)]"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-[#e6e2d6] rounded-2xl px-5 py-4 shadow-[0_8px_24px_-8px_rgba(26,41,71,0.18)]">
            <div className="label-eyebrow">Since 1971</div>
            <div className="font-serif text-xl text-stone-900">
              57 years of gathering.
            </div>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-2 gap-8">
        {[
          {
            k: "Contact Book",
            t: "One Bailey address book",
            d: "Names, phones, emails, and mailing addresses — kept fresh by relatives themselves.",
          },
          {
            k: "Your Family Card",
            t: "Access from your family page",
            d: "Every household has a private card. Sign in with your email + password on any device, or bookmark the invite link to jump straight in.",
          },
        ].map((f) => (
          <div
            key={f.k}
            className="bg-white border border-[#e6e2d6] rounded-2xl p-6 card-lift"
          >
            <div className="label-eyebrow mb-3">{f.k}</div>
            <div className="font-serif text-xl text-stone-900 mb-2">{f.t}</div>
            <p className="text-stone-600 text-sm leading-relaxed">{f.d}</p>
          </div>
        ))}
      </section>

      <footer className="border-t border-[#e6e2d6] py-8 text-center text-sm text-stone-500">
        Built with warmth for the family reunion.
      </footer>
    </div>
  );
}
